import { useEffect, useState } from 'react';
import type { CountryState } from '@firsttable/types';
import { Container } from '@firsttable/web-components/atoms';
import { getUserCity } from '@firsttable/functions';
import { type FilteredRestaurantsCarouselMode } from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import PD6630AllLocations from './PD6630AllLocations';
import PD6630CuisineCarousel from './PD6630CuisineCarousel';
import PD6630RestaurantCarousel, {
  PD6630RestaurantCarouselPlaceholder,
} from './PD6630RestaurantCarousel';
import { findPD6630KnownLocation, getPD6630Locations, type PD6630Region } from './locations';

// No saved location: the largest cities on each site (keyed by SiteConfig.countryCode), in display order.
// The CMS SiteConfig uses `GB` for the UK; the `UK` code in data/index.ts is a separate static mapping.
const FALLBACK_CITY_SLUGS: Record<string, string[]> = {
  NZ: ['/auckland', '/christchurch', '/queenstown-lakes'],
  AU: ['/melbourne', '/sydney', '/brisbane'],
  GB: ['/london', '/bristol', '/brighton'],
  IE: ['/dublin'],
};

const USER_CITY_MODES_BEFORE_CUISINE: FilteredRestaurantsCarouselMode[] = [
  'availableToday',
  'mostPopular',
];

const USER_CITY_MODES_AFTER_CUISINE: FilteredRestaurantsCarouselMode[] = ['newArrivals'];

export type PD6630HomepageRestaurantsProps = {
  countryStates: CountryState[];
  regions?: readonly PD6630Region[];
  countryCode?: string | null;
};

export default function PD6630HomepageRestaurants({
  countryStates,
  regions = [],
  countryCode,
}: PD6630HomepageRestaurantsProps) {
  const locations = getPD6630Locations(countryStates, regions);
  // undefined until mount: the server has no saved city, and reading storage during render would mismatch.
  const [savedCitySlug, setSavedCitySlug] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    setSavedCitySlug(getUserCity());
  }, []);
  const knownCity =
    savedCitySlug === undefined ? undefined : findPD6630KnownLocation(locations, savedCitySlug);
  const carouselsBeforeCuisine = knownCity
    ? USER_CITY_MODES_BEFORE_CUISINE.map((mode) => ({ city: knownCity, mode }))
    : (FALLBACK_CITY_SLUGS[countryCode ?? ''] ?? []).flatMap((slug) => {
        const city = locations.find((location) => location.slug === slug);
        return city ? [{ city, mode: 'mostPopular' as const }] : [];
      });
  const carouselsAfterCuisine = knownCity
    ? USER_CITY_MODES_AFTER_CUISINE.map((mode) => ({ city: knownCity, mode }))
    : [];

  return (
    // No mobile gutter so carousels can scroll to the screen edge; each section insets its own content.
    <Container mt={[32, 40]} mb={[56, 88]} pl={[0, 'm']} pr={[0, 'm']}>
      <div className="flex flex-col gap-10 lg:gap-16">
        {savedCitySlug === undefined ? (
          <>
            <PD6630RestaurantCarouselPlaceholder />
            <PD6630RestaurantCarouselPlaceholder />
          </>
        ) : (
          <>
            {carouselsBeforeCuisine.map(({ city, mode }) => (
              <PD6630RestaurantCarousel key={`${city.id}-${mode}`} city={city} mode={mode} />
            ))}
            {knownCity ? <PD6630CuisineCarousel city={knownCity} /> : null}
            {carouselsAfterCuisine.map(({ city, mode }) => (
              <PD6630RestaurantCarousel key={`${city.id}-${mode}`} city={city} mode={mode} />
            ))}
          </>
        )}
        <PD6630AllLocations locations={locations} />
      </div>
    </Container>
  );
}
